import { ColDef, ColGroupDef } from 'ag-grid-community'
import { intl } from '../../../../../i18n'
import { useRowNumber } from '../../../../page-properties/bulksheet-properties/rowNumber'
import { dragAndDrop } from '../../../../page-properties/bulksheet-properties/dragAndDrop'
import {
  useCreatedBy,
  useCreatedAt,
  useUpdatedBy,
  useUpdatedAt,
} from '../../../../page-properties/bulksheet-properties/changeLog'
import { useMemo } from 'react'
import { RoleRow, roleRowService } from '../../model/RoleRow'
import { MultiSelectCellEditor } from '../../../../containers/BulkSheetView/components/cellEditor/MultiSelectCellEditor'
import { DefaultCellRenderer } from '../../../../containers/BulkSheetView/components/cellRenderer/DefaultCellRenderer'
import { PermissionPolicyOption } from '../../model/PermissionPolicyOption'
import { cellStyle } from './cellStyle'
import { useAttachablePermissionPolicyOptions } from './attachablePermissionPolicyOptions'
import { useCode } from '../../../../page-properties/bulksheet-properties/common'
import { ClientSideSelectFilter } from '../../../../containers/BulkSheetView/components/filter'

type UseColumnDefsProps = {
  permissionPolicyOptions: PermissionPolicyOption[]
}

export const useColumnDefs = ({
  permissionPolicyOptions,
}: UseColumnDefsProps): (ColDef<RoleRow> | ColGroupDef<RoleRow>)[] => {
  const codeCol = useCode('code', { cellStyle })
  const rowNumberCol = useRowNumber()
  const createdByCol = useCreatedBy('createdBy')
  const createdAtCol = useCreatedAt('createdAt')
  const updatedByCol = useUpdatedBy('updatedBy')
  const updatedAtCol = useUpdatedAt('updatedAt')
  const attachablePermissionPolicyOptions =
    useAttachablePermissionPolicyOptions(permissionPolicyOptions)

  const columnDef = useMemo(
    () => [
      {
        field: 'uuid',
        hide: true,
        suppressColumnsToolPanel: true,
      },
      dragAndDrop,
      rowNumberCol,
      codeCol,
      {
        field: 'name',
        headerName: intl.formatMessage({ id: 'roles.column.name' }),
        editable: params =>
          params.data && roleRowService.isEditable(params.data),
        width: 200,
        floatingFilter: true,
        cellRendererParams: {
          validate: (value?: string) => {
            if (!value || value.trim() === '') {
              return intl.formatMessage({ id: 'boolExpression.requiredField' })
            }
            return undefined
          },
        },
        cellStyle,
      },
      {
        field: 'description',
        headerName: intl.formatMessage({ id: 'roles.column.description' }),
        editable: params =>
          params.data && roleRowService.isEditable(params.data),
        width: 300,
        floatingFilter: true,
        cellStyle,
      },
      {
        field: 'permissionPolicyUuids',
        headerName: intl.formatMessage({ id: 'roles.column.policies' }),
        editable: params =>
          params.data && roleRowService.isEditable(params.data),
        width: 600,
        cellEditor: MultiSelectCellEditor,
        cellEditorParams: {
          options: attachablePermissionPolicyOptions,
        },
        cellRenderer: DefaultCellRenderer,
        cellRendererParams: {
          validate: (value?: string[]) => {
            if (!value || value.length === 0) {
              return intl.formatMessage({ id: 'boolExpression.requiredField' })
            }

            // Check if all attachedByDefault policies are included
            const missingDefaultPolicy = permissionPolicyOptions.some(
              p => p.attachedByDefault && !value.includes(p.uuid)
            )
            if (missingDefaultPolicy) {
              return intl.formatMessage({
                id: 'roles.validation.attachedByDefaultRequired',
              })
            }

            // Check if any non-attachable policies are included
            const hasNonAttachablePolicy = permissionPolicyOptions.some(
              p => !p.attachable && value.includes(p.uuid)
            )
            if (hasNonAttachablePolicy) {
              return intl.formatMessage({
                id: 'roles.validation.nonAttachablePolicyIncluded',
              })
            }

            return undefined
          },
        },
        valueFormatter: params => {
          if (!params.value || params.value.length === 0) return ''
          const selectedPolicies = permissionPolicyOptions.filter(p =>
            params.value.includes(p.uuid)
          )
          return selectedPolicies.map(p => p.name).join(',')
        },
        cellStyle,
        filter: ClientSideSelectFilter,
        floatingFilter: true,
        filterParams: {
          valueGetter: (params: { node; context }) => {
            if (!params.node.data) {
              return undefined
            }
            const roleRow = params.node.data as RoleRow

            return permissionPolicyOptions.filter(v =>
              roleRow.permissionPolicyUuids.includes(v.uuid)
            )
          },
          getValue: (o: PermissionPolicyOption) => o.uuid,
          getLabel: (o: PermissionPolicyOption) => o.name,
          filterChangedCallback: () => {
            return
          },
        },
      },
      {
        field: 'inUse',
        headerName: intl.formatMessage({ id: 'roles.column.inUse' }),
        editable: false,
        width: 120,
        valueFormatter: params => {
          return params.value
            ? intl.formatMessage({ id: 'roles.inUse.true' })
            : ''
        },
        cellStyle,
      },
      {
        headerName: intl.formatMessage({ id: 'changeLog' }),
        children: [createdByCol, createdAtCol, updatedByCol, updatedAtCol],
      },
    ],
    [
      rowNumberCol,
      codeCol,
      attachablePermissionPolicyOptions,
      createdByCol,
      createdAtCol,
      updatedByCol,
      updatedAtCol,
      permissionPolicyOptions,
    ]
  )

  return columnDef
}
